/**
 * Slug Utilities
 *
 * Shared slug normalization for property paths, page scopes, file names, and URLs.
 * All consumers (preview, editor, bake scripts) should use these instead of
 * inline slugify implementations to ensure consistent path generation.
 */

/**
 * Convert a string to a URL-safe slug.
 *
 * - Lowercases
 * - Replaces non-alphanumeric sequences (dots, spaces, dashes, etc.) with a single dash
 * - Collapses consecutive dashes (e.g., `foo--bar` → `foo-bar`)
 * - Strips leading/trailing dashes
 */
export function slugify(str: string): string {
  return (str || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Normalize a property_slug value from the API.
 *
 * Handles known data quality issues:
 * - Trailing dots (e.g., `acacia-park-apts.` → `acacia-park-apts`)
 * - Consecutive dashes (e.g., `97--north-oak` → `97-north-oak`)
 * - Other non-alphanumeric characters
 *
 * Falls back to slugifying the property name if slug is empty.
 */
export function normalizePropertySlug(slug: string | null | undefined, propertyName?: string): string {
  const raw = slug || '';
  if (raw) return slugify(raw);
  return propertyName ? slugify(propertyName) : '';
}

/** US state abbreviation → full name lookup. */
export const STATE_NAMES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  DC: 'District of Columbia',
};

/** Full state name → abbreviation (reverse lookup). */
const STATE_ABBRS: Record<string, string> = Object.fromEntries(
  Object.entries(STATE_NAMES).map(([abbr, name]) => [name.toLowerCase(), abbr])
);

/**
 * Get the full state name from an abbreviation or already-full name.
 * Returns the input unchanged if not a recognized abbreviation.
 */
export function getStateName(state: string): string {
  if (!state) return '';
  // If it's a 2-letter abbreviation, expand it
  if (state.length === 2) {
    return STATE_NAMES[state.toUpperCase()] || state;
  }
  return state;
}

/**
 * Get the 2-letter state abbreviation from a full name or abbreviation.
 * Returns the input unchanged if not recognized.
 */
export function getStateAbbr(state: string): string {
  if (!state) return '';
  if (state.length === 2) return state.toUpperCase();
  return STATE_ABBRS[state.toLowerCase()] || state;
}

/**
 * Normalize property data fields that affect path generation.
 *
 * Call this once after fetching property data from the API to ensure
 * all downstream path/scope/filename consumers get clean values.
 *
 * Mutations applied:
 * - `property_slug`: trailing dots removed, consecutive dashes collapsed
 * - `_slug` (if present): same normalization
 * - `property_address_state`: normalized to 2-letter abbreviation (e.g., "Missouri" → "MO")
 *   so all deployed URLs use consistent /apartments/mo/... format
 */
export function normalizePropertyPaths<T extends Record<string, any>>(property: T): T {
  const result: Record<string, any> = { ...property };

  if (result['property_slug']) {
    result['property_slug'] = normalizePropertySlug(
      result['property_slug'],
      result['property_name']
    );
  }

  // Also normalize _slug if set by buildPropertyIndex
  if (result['_slug']) {
    result['_slug'] = normalizePropertySlug(result['_slug'], result['property_name']);
  }

  // Normalize state to 2-letter abbreviation for consistent URL paths
  if (result['property_address_state']) {
    result['property_address_state'] = getStateAbbr(result['property_address_state']);
  }

  return result as T;
}
